import React, { useEffect, useMemo, useState } from 'react';
import env from '@lib/envs/env';
import DefaultTemplate from '@components/shared/templates/DefaultTemplate/DefaultTemplate';
import If from '@components/shared/conditional/If/If';
import Head from 'next/head';
import { useRouter } from 'next/router';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';
import { INavigationProps } from '@components/shared/Navigation/Navigation';
import { useTheme } from '@components/theme';
import ShopNavigationMenu, {
  IShopNavigationMenu,
} from '@components/shared/shop/ShopNavigationMenu';
import { useMaxBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import SortBy from '@components/pages/ShopV3/components/SortBy/SortBy';
import classNames from 'classnames';
import useToggle from '@hooks/useToggle';
import MobileFilters from '@components/pages/ShopV3/components/MobileFilters/MobileFilters';
import FilterCountBadge
  from '@components/pages/ShopV3/components/Sidebar/components/FilterCountBadge/FilterCountBadge';
import Pills from '@components/pages/ShopV3/components/Pills/Pills';
import { ParsedUrlQueryInput, stringify as urlQueryStringify } from 'querystring';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import _ from 'lodash';
import DesktopFilterToggle
  from '@components/pages/ShopV3/components/FilterToggle/Desktop/DesktopFilterToggle';
import MobileFilterToggle
  from '@components/pages/ShopV3/components/FilterToggle/Mobile/MobileFilterToggle';
import {
  IFacetValue,
  ISearchCriteria,
  ISearchFilters,
  ISearchResults,
} from './model/SearchResults';
import styles from './Shop.module.scss';
import Sidebar from './components/Sidebar/Sidebar';
import Breadcrumb, { BreadcrumbItem } from './components/Breadcrumb/Breadcrumb';
import ProductList from './components/ProductList/ProductList';
import { IShopPageData, ShopPageType } from './model/ShopPage';
import ShopHeroBanner from './components/ShopHeroBanner/ShopHeroBanner';
import { IFooterImages } from './components/ShopSuggestions/formatShopSuggestions';
import { getAttributeFamilyFilters } from './model/AttributeFamily';
import { SHOP_SEARCH_URL } from './util/search';

interface IShopProps {
  slugs?: string[];
  shopPageType: ShopPageType;
  searchCriteria: ISearchCriteria;
  searchFilters: ISearchFilters;
  searchResults: ISearchResults;
  footerImages: IFooterImages[];
  shopPageData?: IShopPageData;
  shopNavigationMenu: IShopNavigationMenu[];
}

function replaceUrlPage(url: string, page: number) {
  return url.replace(/\bpage=\d+/, `page=${page}`);
}

function getPageDetailsFromFilters({
  selectedMerchGroup,
  selectedShopGroupChain = [],
  selectedVendorGroupChain = [],
}: ISearchFilters) {
  const breadcrumb: BreadcrumbItem[] = [{
    label: 'Shop',
    url: '/shop'
  }];

  let metaTitle = 'Shop Home Furniture & Décor | Havenly';
  let metaDescription = 'Browse furniture & décor from 200+ retailers in the Havenly Shop.';
  let pageTitle;

  if (selectedShopGroupChain.length > 0) {
    let url = '/shop';
    selectedShopGroupChain.forEach((category) => {
      url = `${url}/${category.slug}`;
      breadcrumb.push({
        label: category.title,
        url,
      });
    });

    const selectedCategory = selectedShopGroupChain[selectedShopGroupChain.length - 1];
    pageTitle = selectedCategory.title;
    metaTitle = `Shop ${selectedCategory.title} | Havenly`;
    metaDescription = `Browse ${selectedCategory.title} in the Havenly Shop.`;
    if (selectedMerchGroup) {
      metaTitle = `${selectedMerchGroup.title} | ${metaTitle}`;
      metaDescription = selectedMerchGroup.description || '';
    }
  } else if (selectedMerchGroup) {
    breadcrumb.push({
      label: selectedMerchGroup.title,
      url: `/shop/${selectedMerchGroup.slug}`,
    });

    pageTitle = selectedMerchGroup.title;
    metaTitle = `${selectedMerchGroup.title} | ${metaTitle}`;
    metaDescription = selectedMerchGroup.description || '';
  } else if (selectedVendorGroupChain.length > 0) {
    let url = '/shop';
    selectedVendorGroupChain.forEach((group) => {
      url = `${url}/${group.slug}`;
      breadcrumb.push({
        label: group.title,
        url,
      });
    });

    const selectedVendorGroup = selectedVendorGroupChain[selectedVendorGroupChain.length - 1];
    pageTitle = selectedVendorGroup.title;
    metaTitle = `${selectedVendorGroup.title} | ${metaTitle}`;
    metaDescription = `Shop ${selectedVendorGroup.title} | Havenly`;
  }

  return {
    metaTitle,
    metaDescription,
    pageTitle,
    breadcrumb,
  };
}

function jsonSchema(breadcrumb: BreadcrumbItem[]) {
  const baseUrl = `https://${env.DOMAIN_NAME}`;

  return [{
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb.map(({ label, url }, index) => (
      {
        '@type': 'ListItem',
        position: index + 1,
        name: label,
        item: `${baseUrl}${url}`
      }
    ))
  }];
}

const Shop = ({
  searchCriteria,
  searchFilters,
  searchResults,
  footerImages,
  shopPageType,
  shopPageData = {
    title: 'Shop',
    featuredItems: [],
  },
  shopNavigationMenu,
}: IShopProps) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMaxBreakpoint('desktop');
  const [isSidebarExpanded, toggleSidebar] = useToggle(false);
  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);

  const query: ParsedUrlQueryInput = { ...router.query };
  const { attributeFamilies } = query;
  const [selectedFiltersIds, setSelectedFiltersIds] = useState<Array<string>>([]);

  useEffect(() => {
    if (attributeFamilies) {
      if (typeof attributeFamilies === 'string') {
        setSelectedFiltersIds([attributeFamilies]);
      } else {
        setSelectedFiltersIds(attributeFamilies as Array<string>);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedFiltersIds) {
      query.attributeFamilies = selectedFiltersIds;
      query.page = 1;

      router.push(`${router.pathname}?${urlQueryStringify(query)}`);
    }
  }, [selectedFiltersIds]);

  const {
    metaTitle,
    metaDescription,
    pageTitle,
    breadcrumb
  } = getPageDetailsFromFilters(searchFilters);

  if (router.query.ismissing) {
    const missingProductAlert = useAlert(TYPES.ERROR);
    const { missingproduct, missingvendor } = router.query;
    useEffect(() => {
      missingProductAlert(`Sorry, the ${missingproduct || 'product'}
        from ${missingvendor || `this vendor`} is no longer available`);
    }, []);
  }

  function getFilterCount() {
    const appliedFilters = Object.keys(router.query)
      .filter((key) => {
        return key !== 'text' && key !== 'slugs';
      });

    if (appliedFilters.includes('priceMin') && appliedFilters.includes('priceMax')) {
      appliedFilters.splice(appliedFilters.indexOf('priceMin'), 1);
    }

    return appliedFilters.map((key) => (router.query[key]))
      .reduce((prev, curr) => {
        return Array.isArray(curr) ? curr.length + prev : prev + 1;
      }, 0);
  }

  const filterCount = getFilterCount();

  const navProps: INavigationProps = {
    isFixed: true,
    isTransparent: Boolean(isMobile),
    showSearch: Boolean(isMobile),
    showGetStarted: false,
    shopNavigationMenu,
    onSearch: (search: string) => {
      const text = search?.trim();
      router.push({
        pathname: SHOP_SEARCH_URL,
        query: {
          text
        }
      });
    },
  };

  const totalPages = useMemo(() => {
    return Math.max(1, searchResults.total / searchResults.pageSize);
  }, [searchResults.total, searchResults.pageSize]);

  const attributeFamilyFilters = getAttributeFamilyFilters(searchResults);

  const selectedFamilyFilters = _.flatten(Object.keys(attributeFamilyFilters).map((parent) => {
    return attributeFamilyFilters[parent];
  })).filter((item) => {
    return selectedFiltersIds.includes(item.id);
  });

  function isValueSelected(value: IFacetValue) {
    return selectedFiltersIds.includes(value.id!);
  }

  function handleNavigate(value: IFacetValue): void {
    analyticsTrack('shop_filter_clicked');

    if (isValueSelected(value)) {
      setSelectedFiltersIds(
        [...selectedFiltersIds.filter((val) => (val !== value.id))]
      );
    } else {
      setSelectedFiltersIds([...selectedFiltersIds, value.id]);
    }
  }

  const handleClearParams = () => {
    setSelectedFiltersIds([]);
  };

  const [showMobileFilters, toggleMobileFilters] = useToggle(false);

  const shopNavigationIsFixed = true;

  return (
    <DefaultTemplate
      title={metaTitle}
      description={metaDescription}
      jsonSchema={jsonSchema(breadcrumb)}
      canonicalLink={`https://${env.DOMAIN_NAME}${router.asPath.split('?')[0]}`}
      noIndex={searchResults.page > 1}
      navProps={navProps}
      className={theme.styles.Body}
      fonts={theme.fontLinks}
      checkUnbounceBannerHeight={
        (height) => setUnbounceBannerHeight(height)
      }
    >
      {
        showMobileFilters && (
          <MobileFilters
            filters={searchFilters}
            searchResults={searchResults}
            onToggleMobileFilters={toggleMobileFilters}
            shopPageType={shopPageType}
            isExpanded={isSidebarExpanded}
            attributeFamilies={{
              attributeFamilyFilters,
              selectedFilterIds: selectedFiltersIds,
              onFilterSelected: handleNavigate
            }}
            onClearAll={handleClearParams}
          />
        )
      }

      <div className={styles.Content}>
        <If condition={searchResults.page > 1}>
          <Head>
            <link rel="prev" href={`${(replaceUrlPage(router.asPath, searchResults.page - 1))}`} />
            <If condition={searchResults.page < totalPages}>
              <link rel="next" href={`${replaceUrlPage(router.asPath, searchResults.page + 1)}`} />
            </If>
          </Head>
        </If>

        <div className={styles.ShopPage}>

          <ShopNavigationMenu
            shopNavigationMenu={shopNavigationMenu}
            isFixed={shopNavigationIsFixed}
            unbounceBannerHeight={unbounceBannerHeight}
          />

          <div className={classNames(styles.Header, {
            [styles.ShopNavigationIsFixed]: shopNavigationIsFixed
          })}
          >
            <ShopHeroBanner
              title={shopPageData.title}
              description={shopPageData?.description}
              image={shopPageData?.image}
            />
            <Breadcrumb className={styles.Breadcrumb} items={breadcrumb} />
          </div>

          <div className={styles.ShopContainer}>
            <div
              className={
                classNames(styles.FilterBar)
              }
            >
              <div
                className={classNames(styles.FilterButtonWrapper, {
                  [styles.Hidden]: !isSidebarExpanded,
                })}
              >
                <DesktopFilterToggle
                  onClick={toggleSidebar}
                  isSidebarExpanded={isSidebarExpanded}
                  filterCount={filterCount}
                />
              </div>

              <div className={styles.PillsSortByFiltersWrapper}>
                <Pills
                  items={selectedFamilyFilters}
                  onClickItem={handleNavigate}
                  onClearAll={handleClearParams}
                />
                <SortBy />
              </div>
            </div>

            <div className={styles.Shop}>
              <Sidebar
                className={classNames(styles.Sidebar, {
                  [styles.Hidden]: !isSidebarExpanded
                })}
                filters={searchFilters}
                searchResults={searchResults}
                isExpanded={isSidebarExpanded}
                shopPageType={shopPageType}
                attributeFamilies={{
                  attributeFamilyFilters,
                  selectedFilterIds: selectedFiltersIds,
                  onFilterSelected: handleNavigate
                }}
              />
              <div className={styles.ProductListWrapper}>
                <div className={styles.MobileFilterButtonWrapper}>
                  <MobileFilterToggle onClick={toggleMobileFilters} />
                  {
                    filterCount > 0 && (
                      <FilterCountBadge count={filterCount} />
                    )
                  }
                </div>
                <ProductList
                  className={styles.ProductList}
                  filters={searchFilters}
                  searchCriteria={searchCriteria}
                  searchResults={searchResults}
                  featuredItems={shopPageData?.featuredItems}
                  title={pageTitle}
                  footerImages={footerImages}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultTemplate>
  );
};

export default Shop;
